/**
 * Follows one pointer from a pane's divider. The width to show arrives at most once per frame;
 * `onEnd` gets the last width, whether the drag was completed or called off, and whether it left
 * the pane collapsed. Returns the function that calls the drag off.
 *
 * With `collapseBelow`, a pointer further left than that width collapses the pane and one further
 * right opens it again; `onCollapse` hears each crossing. The drag may start on a collapsed pane
 * (`startCollapsed`, `startWidth` 0): pulling its edge out opens it.
 */
export function trackPaneWidthDrag(
  start: { clientX: number; pointerId: number },
  {
    startWidth,
    minWidth,
    maxWidth,
    collapseBelow,
    startCollapsed = false,
    onWidth,
    onCollapse,
    onEnd,
  }: {
    startWidth: number;
    minWidth: number;
    maxWidth: number;
    collapseBelow?: number;
    startCollapsed?: boolean;
    onWidth: (width: number) => void;
    onCollapse?: (collapsed: boolean) => void;
    onEnd: (width: number, committed: boolean, collapsed: boolean) => void;
  }
) {
  const { clientX: startX, pointerId } = start;
  const collapses = (width: number) =>
    collapseBelow !== undefined && width < collapseBelow;
  let nextWidth = startWidth;
  let displayedWidth = startWidth;
  let nextCollapsed = startCollapsed;
  let displayedCollapsed = startCollapsed;
  let frame: number | undefined;
  const pending = () =>
    nextCollapsed !== displayedCollapsed ||
    (!nextCollapsed && nextWidth !== displayedWidth);
  const applyWidth = () => {
    frame = undefined;
    // A collapsed pane keeps the width it had; it shows the new one once it is open again.
    if (!nextCollapsed && displayedWidth !== nextWidth) {
      displayedWidth = nextWidth;
      onWidth(nextWidth);
    }
    if (displayedCollapsed !== nextCollapsed) {
      displayedCollapsed = nextCollapsed;
      onCollapse?.(nextCollapsed);
    }
  };
  const trackPointer = (event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    const width = Math.round(startWidth + event.clientX - startX);
    nextCollapsed = collapses(width);
    nextWidth = Math.min(maxWidth, Math.max(minWidth, width));
  };
  const onPointerMove = (event: PointerEvent) => {
    trackPointer(event);
    if (event.pointerId === pointerId && pending() && frame === undefined)
      frame = window.requestAnimationFrame(applyWidth);
  };
  const finish = (committed: boolean) => {
    window.cancelAnimationFrame(frame ?? 0);
    frame = undefined;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    window.removeEventListener('blur', onBlur);
    if (committed) applyWidth();
    onEnd(nextWidth, committed, displayedCollapsed);
  };
  const onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    trackPointer(event);
    finish(true);
  };
  const onPointerCancel = (event: PointerEvent) => {
    if (event.pointerId === pointerId) finish(true);
  };
  const onBlur = () => finish(true);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerCancel);
  window.addEventListener('blur', onBlur);
  return () => finish(false);
}
